import { CSSProperties, ComponentPropsWithoutRef, memo, useMemo } from 'react';

import { cn } from '@/lib/utils';

import { hardwareAcceleration } from '../../utils/performanceUtils';

interface MarqueeProps extends ComponentPropsWithoutRef<'div'> {
  /**
   * Optional CSS class name to apply custom styles
   */
  className?: string;
  /**
   * Whether to reverse the animation direction
   * @default false
   */
  reverse?: boolean;
  /**
   * Whether to pause the animation on hover
   * @default false
   */
  pauseOnHover?: boolean;
  /**
   * Content to be displayed in the marquee
   */
  children: React.ReactNode;
  /**
   * Whether to animate vertically instead of horizontally
   * @default false
   */
  vertical?: boolean;
  /**
   * Number of times to repeat the content
   * @default 4
   */
  repeat?: number;
}

export const Marquee = memo(function Marquee({
  className,
  reverse = false,
  pauseOnHover = false,
  children,
  vertical = false,
  repeat = 4,
  ...props
}: MarqueeProps) {
  const elementStyle = useMemo<CSSProperties>(
    () => ({
      ...hardwareAcceleration,
      willChange: 'transform',
      contain: 'layout style paint',
    }),
    []
  );

  const repeatedElements = useMemo(() => {
    return Array.from({ length: repeat }, (_, i) => (
      <div
        key={i}
        className={cn('flex shrink-0 justify-around [gap:var(--gap)]', {
          'animate-marquee flex-row': !vertical,
          'animate-marquee-vertical flex-col': vertical,
          'group-hover:[animation-play-state:paused]': pauseOnHover,
          '[animation-direction:reverse]': reverse,
        })}
        style={elementStyle}
      >
        {children}
      </div>
    ));
  }, [repeat, vertical, pauseOnHover, reverse, children, elementStyle]);

  const containerStyle = useMemo<CSSProperties>(
    () => ({
      ...hardwareAcceleration,
      contain: 'layout style',
      ...props.style,
    }),
    [props.style]
  );

  return (
    <div
      {...props}
      className={cn(
        'group flex overflow-hidden p-2 [--duration:40s] [--gap:1rem] [gap:var(--gap)]',
        {
          'flex-row': !vertical,
          'flex-col': vertical,
        },
        className
      )}
      style={containerStyle}
    >
      {repeatedElements}
    </div>
  );
});
